var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

// cache
import Cache from './Cache';

// constants
import { INVALID_FIRST_PARAMETER_ERROR } from './constants';

// types


// utils
import { compose, createAddPropertiesToFunction, createCurriableOptionMethod, createGetCacheKey, createSetNewCachedValue, getDefaultedOptions, isFunction, isPlainObject } from './utils';

/**
 * @module moize
 */

/**
 * @function moize
 *
 * @description
 * store cached values returned from calling method with arguments to avoid reprocessing data from same arguments
 *
 * @example
 * import moize from 'moize';
 *
 * // standard implementation
 * const fn = (foo, bar) => {
 *  return `${foo} ${bar}`;
 * };
 * const memoizedFn = moize(fn);
 *
 * // implementation with options
 * const fn = async (id) => {
 *  return get(`http://foo.com/${id}`);
 * };
 * const memoizedFn = moize(fn, {
 *  isPromise: true,
 *  maxSize: 5
 * });
 *
 * @param {function} functionOrComposableOptions method to memoize
 * @param {Options} [passedOptions={}] options to customize how the caching is handled
 * @param {boolean} [passedOptions.isPromise=false] is the function return expected to be a promise to resolve
 * @param {number} [passedOptions.maxAge=Infinity] the maximum age the value should persist in cache
 * @param {number} [passedOptions.maxArgs=Infinity] the maximum number of arguments to be used in serializing the keys
 * @param {number} [passedOptions.maxSize=Infinity] the maximum size of the cache to retain
 * @param {function} [passedOptions.promiseLibrary=Promise] promise library to use for resolution / rejection
 * @param {function} [passedOptions.serializeFunctions=false] should function parameters be serialized as well
 * @param {function} [passedOptions.serializer] method to serialize arguments with for cache storage
 * @returns {Function} higher-order function which either returns from cache or newly-computed value
 */
var moize = function moize(functionOrComposableOptions) {
  var passedOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (isPlainObject(functionOrComposableOptions)) {
    return function (fnOrOptions) {
      var otherOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (isPlainObject(fnOrOptions)) {
        return moize(_extends({}, functionOrComposableOptions, fnOrOptions));
      }

      return moize(fnOrOptions, _extends({}, functionOrComposableOptions, otherOptions));
    };
  }

  if (!isFunction(functionOrComposableOptions)) {
    throw new TypeError(INVALID_FIRST_PARAMETER_ERROR);
  }

  var isComposed = functionOrComposableOptions.isMoized;
  // $FlowIgnore if the function is already moized, it has an originalFunction property on it
  var fn = isComposed ? functionOrComposableOptions.originalFunction : functionOrComposableOptions;

  var options = getDefaultedOptions(isComposed ? _extends({}, functionOrComposableOptions.options, passedOptions) : passedOptions);

  var cache = new Cache(options);

  var addPropertiesToFunction = createAddPropertiesToFunction(cache, fn, options);
  var getCacheKey = createGetCacheKey(cache, options);
  var setNewCachedValue = createSetNewCachedValue(cache, options);

  return addPropertiesToFunction(function () {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var key = getCacheKey(args);

    return cache.size && cache.has(key) ? cache.get(key) : setNewCachedValue(key, fn.apply(this, args));
  });
};

/**
 * @function compose
 *
 * @description
 * method to compose moized methods and return a single moized function
 *
 * @param {...Array<(function)>} functions the functions to compose
 * @returns {function(...Array<*>): *} the composed function
 */
moize.compose = compose;

/**
 * @NOTE provide default export for TS to be able to import directly
 */
moize.default = moize;

/**
 * @function isMoized
 *
 * @description
 * is the fn passed a moized function
 *
 * @param {*} fn the object to test
 * @returns {boolean} is fn a moized function
 */
moize.isMoized = function (fn) {
  return isFunction(fn) && !!fn.isMoized;
};

/**
 * @function maxAge
 *
 * @description
 * a moized method where the age of the cache is limited to the number of milliseconds passed
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.maxAge = createCurriableOptionMethod(moize, 'maxAge');

/**
 * @function maxArgs
 *
 * @description
 * a moized method where the number of arguments used for determining cache is limited to the value passed
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.maxArgs = createCurriableOptionMethod(moize, 'maxArgs');

/**
 * @function maxSize
 *
 * @description
 * a moized method where the total size of the cache is limited to the value passed
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.maxSize = createCurriableOptionMethod(moize, 'maxSize');

/**
 * @function promise
 *
 * @description
 * a moized method specific to caching resolved promise / async values
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.promise = moize({
  isPromise: true
});

/**
 * @function react
 *
 * @description
 * a moized method specific to caching React components
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.react = moize({
  isReact: true
});

/**
 * @function reactSimple
 *
 * @description
 * a moized method specific to caching React components, only keeping the most recently-cached version
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.reactSimple = compose(moize.react, moize.maxSize(1));

/**
 * @function serialize
 *
 * @description
 * a moized method where the arguments passed are cached based on their serialized values
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.serialize = moize({
  serialize: true
});

/**
 * @function simple
 *
 * @description
 * a moized method where only the most recent key => value combination is cached
 *
 * @param {...Array<*>} functions the functions to compose
 * @returns {*} the moized function
 */
moize.simple = moize.maxSize(1);

export default moize;


//////////////////
// WEBPACK FOOTER
// ./~/filtermatrix/~/moize/es/index.js
// module id = 2175
// module chunks = 0